<template>
  <article class="news-card" :class="{'news-card--read': isInHistory}">
    <!-- Header con color sutil o miniatura de YouTube -->
    <div v-if="item.sourceType === 'youtube' && item.image" class="news-card__thumbnail">
      <img :src="item.image" :alt="item.title" @error="onImageError" />
      <div class="youtube-play-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </div>
    <div v-else class="news-card__header">
    </div>

    <!-- Avatar circular con iniciales -->
    <div v-if="item.sourceType !== 'youtube'" class="news-card__avatar">
      <div class="avatar-circle"
           :class="`avatar-circle--${item.sourceCategory.toLowerCase()}`">
        <span class="avatar-initials">{{ getCategoryInitials(item.sourceCategory) }}</span>
      </div>
    </div>

    <!-- Contenido del card -->
    <div class="news-card__content" :class="{
      'news-card__content--youtube': item.sourceType === 'youtube',
      'news-card__content--no-description': !item.description && item.sourceType === 'notice'
    }">
      <h3 class="news-card__title">{{ item.title }}</h3>
      <p class="news-card__category">{{ item.sourceTitle || item.sourceCategory || item.category }}</p>
      <p class="news-card__description" v-if="item.description">{{ item.description }}</p>
    </div>

    <!-- Metadata: fecha, favorito y leído -->
    <div class="news-card__metadata">
      <span class="metadata-date">
        {{ timeAgo(item.pubDate) }}
        <span v-if="item.sourceCountry" class="metadata-country"> • {{ item.sourceCountry }}</span>
      </span>
      <div class="metadata-actions">
        <!-- Botón compartir -->
        <div class="share-btn-wrapper" v-if="!isModal">
          <button
            @click.stop="shareCard"
            class="metadata-btn"
            :class="{'metadata-btn--copied': showCopied}"
            :title="showCopied ? '¡Copiado!' : 'Compartir'"
          >
            <svg v-if="!showCopied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>

          <!-- Tooltip "Enlace copiado" -->
          <transition name="tooltip-fade">
            <div v-if="showCopied" class="share-tooltip">
              Enlace copiado
            </div>
          </transition>
        </div>

        <!-- Botón favorito -->
        <div class="favorite-btn-wrapper">
          <button
            @click.stop="toggleFavorite"
            class="metadata-btn"
            :class="{'metadata-btn--active': isFavorite}"
            :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </button>

          <!-- Tooltip de favorito -->
          <transition name="tooltip-fade">
            <div v-if="showFavoriteTooltip" class="favorite-tooltip">
              {{ favoriteTooltipText }}
            </div>
          </transition>
        </div>

        <!-- Indicador de leído -->
        <button
          v-if="isInHistory"
          class="metadata-btn metadata-btn--active"
          title="Leído"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Botón leer más -->
    <div class="news-card__footer">
      <button class="news-card__button" @click.stop="openLink">
        Leer más
      </button>
    </div>
  </article>
</template>

<script>
import { timeAgo, hostname } from '../../../utils/time.js';
import bookmarksService from '../../../services/bookmarks.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';

export default {
  name: 'NewsCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Forzar reactividad con un timestamp
      updateTrigger: 0,
      // Estado del botón compartir
      showCopied: false,
      // Estado del tooltip de favorito
      showFavoriteTooltip: false,
      favoriteTooltipText: ''
    };
  },
  computed: {
    isFavorite() {
      // Usar updateTrigger para forzar reactividad
      this.updateTrigger;
      return bookmarksService.isFavorite(this.item.id || this.item.link);
    },
    isInHistory() {
      // Usar updateTrigger para forzar reactividad
      this.updateTrigger;
      return bookmarksService.isInHistory(this.item.id || this.item.link);
    }
  },
  methods: {
    timeAgo,
    hostname,
    openLink() {
      window.open(this.item.link, '_blank', 'noopener,noreferrer');

      // Agregar al historial automáticamente
      bookmarksService.addToHistory(this.item);
    },
    toggleFavorite() {
      if (this.isFavorite) {
        bookmarksService.removeFromFavorites(this.item.id || this.item.link);
        this.favoriteTooltipText = 'Quitado de favoritos';
      } else {
        bookmarksService.addToFavorites(this.item);
        this.favoriteTooltipText = 'Agregado a favoritos';
      }

      // Mostrar tooltip
      this.showFavoriteTooltip = true;
      setTimeout(() => {
        this.showFavoriteTooltip = false;
      }, 2000);
    },
    getSourceClass(category) {
      if (!category) return 'source--default';
      
      // Mapeo basado en las categorías de tu combineFeeds.js
      const sourceClasses = {
        'OpenAI': 'source--openai',
        'GoogleNews_ML': 'source--google',
        'GoogleNews_AI': 'source--google',
        'GoogleNews': 'source--google',
        'DeepMind': 'source--deepmind',
        'Xataka': 'source--xataca',
        'HuggingFace': 'source--huggingface',
        'KDnuggets': 'source--kdnuggets',
        'Towards': 'source--towardsdatascience',
        'arXiv_LG': 'source--arxiv',
        'arXiv_AI': 'source--arxiv',
        'Reddit_LocalLLaMA': 'source--reddit',
        'Reddit_MachineLearning': 'source--reddit',
        'Reddit_ClaudeAI': 'source--reddit',
        'TechCrunch_AI': 'source--techcrunch',
        'MIT_AI': 'source--mit',
        'MachineLearningMastery': 'source--mlmastery',
        'anthropic': 'source--anthropic',
        'Wired_ES': 'source--wired',
        'CNN_Espanol': 'source--cnn',
        'El_Pais': 'source--elpais',
        'Euronews': 'source--euronews',
        'ElComercio_PE': 'source--elcomercio',
        'LaNacion_AR': 'source--lanacion',
        'fastai': 'source--fastai',
        'YouTube_XavierMitjana': 'source--youtube',
        'TikTok_MigueBaena': 'source--tiktok',
        'TikTok_AngelAparicio': 'source--tiktok',
        'TikTok_IAHipster': 'source--tiktok',
      };
      
      // Buscar coincidencia exacta primero
      if (sourceClasses[category]) {
        return sourceClasses[category];
      }
      
      // Buscar coincidencia parcial
      for (const [key, className] of Object.entries(sourceClasses)) {
        if (category.includes(key) || key.includes(category)) {
          return className;
        }
      }
      
      return 'source--default';
    },
    onImageError(event) {
      // Si la imagen falla al cargar, ocultar el elemento
      event.target.style.display = 'none';
    },
    generatePattern(text) {
      // Generar un hash simple del texto
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    },
    getContributionPattern() {
      // Generar patrón de 1 fila que llene todo el ancho
      const squares = [];
      const baseHash = this.generatePattern(this.item.title + this.item.id);
      const totalSquares = 35; // 1 fila con ~35 cuadrados

      for (let i = 0; i < totalSquares; i++) {
        const hash = this.generatePattern(this.item.title + i + baseHash);
        // Generar opacidad entre 0.3 y 1.0 para visibilidad
        const opacity = 0.3 + ((hash % 70) / 100);
        squares.push(opacity);
      }

      return squares;
    },
    getCategoryInitials(category) {
      // Mapeo de categorías a iniciales personalizadas
      const initialsMap = {
        'HuggingFace': 'HF',
        'KDnuggets': 'KD',
        'TechCrunch_AI': 'TC',
        'GoogleNews_AI': 'GN',
        'GoogleNews_ML': 'GM',
        'GoogleNews': 'GN',
        'arXiv_LG': 'AL',
        'arXiv_AI': 'AA',
        'DeepMind': 'DM',
        'OpenAI': 'OA',
        'MIT_AI': 'MI',
        'Xataka': 'XT',
        'Towards': 'TD',
        'MachineLearningMastery': 'ML',
        'Reddit_MachineLearning': 'RM',
        'Reddit_LocalLLaMA': 'RL',
        'Reddit_ClaudeAI': 'RC',
        'YouTube_AI': 'YT',
        'YouTube_CodificandoBits': 'CB',
        'YouTube_DotCSV': 'DC',
        'YouTube_DotCSV_Lab': 'DL',
        'YouTube_la_inteligencia_artificial': 'IA',
        'YouTube_XavierMitjana': 'XM',
        'AWS': 'AW',
        'Nvidia': 'NV',
        'anthropic': 'AN',
        'Wired_ES': 'WD',
        'CNN_Espanol': 'CN',
        'El_Pais': 'EP',
        'Euronews': 'EU',
        'ElComercio_PE': 'EC',
        'LaNacion_AR': 'LN',
        'fastai': 'FA',
        'TikTok_MigueBaena': 'MB',
        'TikTok_AngelAparicio': 'AA',
        'TikTok_IAHipster': 'IH',
        'GoogleAI': 'GA',
        'MetaAI': 'MA',
        'Ollama': 'OL',
        'Groq': 'GQ'
      };

      // Buscar en el mapa (case insensitive)
      const key = Object.keys(initialsMap).find(k => k.toLowerCase() === category.toLowerCase());
      if (key) return initialsMap[key];

      // Fallback: tomar las primeras 2 letras
      return category.substring(0, 2).toUpperCase();
    },
    handleBookmarkUpdate() {
      // Incrementar el trigger para forzar re-cálculo de computed properties
      this.updateTrigger++;
    },
    async shareCard() {
      const success = await copyShareUrl(this.item);

      if (success) {
        // Mostrar feedback visual
        this.showCopied = true;
        setTimeout(() => {
          this.showCopied = false;
        }, 2000);
      } else {
        console.error('Failed to copy share URL');
      }
    }
  },
  mounted() {
    // Escuchar cambios en bookmarks
    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  },
  beforeUnmount() {
    // Limpiar listener
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  }
};
</script>

<style scoped>
/* Nuevo diseño tipo profile card */
.news-card {
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 14px;
  padding: 0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}

/* Header con color sutil */
.news-card__header {
  width: 100%;
  height: 24px;
  position: relative;
  overflow: hidden;
  background: #262c34;
}

/* Miniatura de YouTube */
.news-card__thumbnail {
  width: 100%;
  height: 150px;
  position: relative;
  overflow: hidden;
  background: #000;
}

.news-card__thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .news-card__thumbnail img {
  transform: scale(1.05);
}

.youtube-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  height: 68px;
  background: rgba(255, 0, 0, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.news-card:hover .youtube-play-icon {
  background: rgba(255, 0, 0, 1);
  transform: translate(-50%, -50%) scale(1.1);
}

/* Avatar circular con iniciales */
.news-card__avatar {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.avatar-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid #1c2127;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Color por defecto para fuentes sin color propio */
  background: linear-gradient(135deg, #6b7280, #4b5563);
}

.avatar-initials {
  font-size: 18px;
  font-weight: 700;
  color: white;
  user-select: none;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Colores de avatar por categoría - tonos balanceados */
.avatar-circle--huggingface {
  background: linear-gradient(135deg, #f0b454, #d89b3d);
}

.avatar-circle--kdnuggets {
  background: linear-gradient(135deg, #e86ba3, #d15589);
}

.avatar-circle--techcrunch_ai {
  background: linear-gradient(135deg, #5dca73, #4ab862);
}

.avatar-circle--googlenews_ai,
.avatar-circle--googlenews_ml,
.avatar-circle--googlenews {
  background: linear-gradient(135deg, #5b9bd5, #4a87c2);
}

.avatar-circle--arxiv_lg,
.avatar-circle--arxiv_ai {
  background: linear-gradient(135deg, #9d7ec9, #8767b5);
}

.avatar-circle--deepmind {
  background: linear-gradient(135deg, #5b9bd5, #4178be);
}

.avatar-circle--openai {
  background: linear-gradient(135deg, #3dbd9b, #2fa884);
}

.avatar-circle--mit_ai {
  background: linear-gradient(135deg, #e8697d, #d45468);
}

.avatar-circle--xataka {
  background: linear-gradient(135deg, #f0a054, #d8893d);
}

.avatar-circle--towards {
  background: linear-gradient(135deg, #5dca8e, #4ab877);
}

.avatar-circle--machinelearningmastery {
  background: linear-gradient(135deg, #de79b1, #ca669d);
}

.avatar-circle--reddit_machinelearning,
.avatar-circle--reddit_localllama,
.avatar-circle--reddit_claudeai {
  background: linear-gradient(135deg, #ff6b47, #e85a38);
}

.avatar-circle--youtube_ai,
.avatar-circle--youtube_codificando_bits,
.avatar-circle--youtube_dotcsv,
.avatar-circle--youtube_dotcsv_lab,
.avatar-circle--youtube_la_inteligencia_artificial {
  background: linear-gradient(135deg, #ff0000, #cc0000);
}

.avatar-circle--aws {
  background: linear-gradient(135deg, #ff9900, #ec7211);
}

.avatar-circle--nvidia {
  background: linear-gradient(135deg, #76b900, #669900);
}

.avatar-circle--anthropic {
  background: linear-gradient(135deg, #d97706, #b45309);
}

.avatar-circle--wired_es {
  background: linear-gradient(135deg, #1a1a1a, #333333);
}

.avatar-circle--cnn_espanol {
  background: linear-gradient(135deg, #cc0000, #aa0000);
}

.avatar-circle--el_pais {
  background: linear-gradient(135deg, #1976d2, #1565c0);
}

.avatar-circle--euronews {
  background: linear-gradient(135deg, #ffd700, #ffb700);
}

.avatar-circle--elcomercio_pe {
  background: linear-gradient(135deg, #0066cc, #0052a3);
}

.avatar-circle--lanacion_ar {
  background: linear-gradient(135deg, #004b93, #003366);
}

.avatar-circle--fastai {
  background: linear-gradient(135deg, #00a6d6, #0088b3);
}

.avatar-circle--youtube_xaviermitjana {
  background: linear-gradient(135deg, #ff0000, #cc0000);
}

.avatar-circle--googleai {
  background: linear-gradient(135deg, #4285f4, #3367d6);
}

.avatar-circle--metaai {
  background: linear-gradient(135deg, #0082fb, #0064e0);
}

.avatar-circle--ollama {
  background: linear-gradient(135deg, #3f3f46, #18181b);
}

.avatar-circle--groq {
  background: linear-gradient(135deg, #f55036, #d63c24);
}

.avatar-circle--tiktok_miguebaena,
.avatar-circle--tiktok_angelaparicio,
.avatar-circle--tiktok_iahipster {
  background: linear-gradient(135deg, #00f2ea, #ff0050);
}

.news-card__source {
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* OpenAI - Verde esmeralda */
.source--openai {
  background: linear-gradient(135deg, #10a37f, #0d8c6b);
  color: white;
}

/* Google News - Azul Google */
.source--google {
  background: linear-gradient(135deg, #4285f4, #2a75f3);
  color: white;
}

/* DeepMind - Turquesa */
.source--deepmind {
  background: linear-gradient(135deg, #00d4aa, #00b894);
  color: white;
}

/* Xataka - Azul intenso */
.source--xataca {
  background: linear-gradient(135deg, #1877f2, #0e5dc6);
  color: white;
}

/* Anthropic - Naranja dorado */
.source--anthropic {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

/* Hugging Face - Amarillo dorado */
.source--huggingface {
  background: linear-gradient(135deg, #ffb000, #e59a00);
  color: white;
}

/* KDnuggets - Rosa coral */
.source--kdnuggets {
  background: linear-gradient(135deg, #ff6b9d, #e55a88);
  color: white;
}

/* Towards Data Science - Púrpura */
.source--towardsdatascience {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

/* arXiv - Rojo burdeos */
.source--arxiv {
  background: linear-gradient(135deg, #b91c1c, #991b1b);
  color: white;
}

/* Reddit - Naranja Reddit */
.source--reddit {
  background: linear-gradient(135deg, #ff4500, #e03e00);
  color: white;
}

/* TechCrunch - Verde lima */
.source--techcrunch {
  background: linear-gradient(135deg, #0cce6b, #0ab259);
  color: white;
}

/* MIT - Azul cian */
.source--mit {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  color: white;
}

/* Machine Learning Mastery - Verde bosque */
.source--mlmastery {
  background: linear-gradient(135deg, #059669, #047857);
  color: white;
}

/* News - Azul índigo */
.source--news {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: white;
}

/* Wired - Negro/Gris oscuro */
.source--wired {
  background: linear-gradient(135deg, #1a1a1a, #333333);
  color: white;
}

/* CNN - Rojo CNN */
.source--cnn {
  background: linear-gradient(135deg, #cc0000, #aa0000);
  color: white;
}

/* El País - Azul El País */
.source--elpais {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

/* Euronews - Amarillo dorado */
.source--euronews {
  background: linear-gradient(135deg, #ffd700, #ffb700);
  color: white;
}

/* El Comercio - Azul oscuro */
.source--elcomercio {
  background: linear-gradient(135deg, #0066cc, #0052a3);
  color: white;
}

/* La Nación - Azul marino */
.source--lanacion {
  background: linear-gradient(135deg, #004b93, #003366);
  color: white;
}

/* Fast AI - Azul cian */
.source--fastai {
  background: linear-gradient(135deg, #00a6d6, #0088b3);
  color: white;
}

/* TikTok - Gradiente característico */
.source--tiktok {
  background: linear-gradient(135deg, #00f2ea, #ff0050);
  color: white;
}

/* YouTube - Rojo YouTube (reutiliza el existente si está definido arriba) */
.source--youtube {
  background: linear-gradient(135deg, #ff0000, #cc0000);
  color: white;
}

/* Default - Gris */
.source--default {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.news-card__time {
  color: #7d8694;
  font-size: 12px;
  font-weight: 500;
}

/* Contenido del card */
.news-card__content {
  padding: 66px 16px 12px;
  text-align: center;
  flex: 1;
}

/* Contenido para YouTube (sin avatar, más cerca de la miniatura) */
.news-card__content--youtube {
  padding: 14px 16px 12px;
}

.news-card__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: #e1e7ef;
  margin: 0 0 6px 0;
  overflow-wrap: anywhere;
}

.news-card__category {
  font-size: 13px;
  font-weight: 500;
  color: #4a9eff;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.news-card__description {
  font-size: 13px;
  line-height: 1.5;
  color: #9ca3af;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news-card__meta {
  padding: 0 20px;
  margin-bottom: 12px;
}

.news-card__domain {
  color: #4a9eff;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
}

/* Metadata: fecha y acciones */
.news-card__metadata {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid #2d333b;
  margin: 0 16px;
}

.metadata-date {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
}

.metadata-country {
  color: #7d8694;
  font-size: 12px;
  font-weight: 400;
}

.metadata-actions {
  display: flex;
  gap: 8px;
}

.metadata-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1.5px solid #2d333b;
  background: #1c2127;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.metadata-btn:hover {
  border-color: #4a5568;
  background: #262c34;
}

.metadata-btn--active {
  background: white;
  border-color: white;
  color: #1c2127;
}

.metadata-btn--active:hover {
  background: #f3f4f6;
  border-color: #f3f4f6;
}

.metadata-btn--copied {
  background: #34d399;
  border-color: #34d399;
  color: white;
}

.metadata-btn--copied:hover {
  background: #10b981;
  border-color: #10b981;
}

/* Wrapper del botón compartir con tooltip */
.share-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip "Enlace copiado" */
.share-tooltip {
  /* Anclado a la derecha, igual que el de favorito */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #34d399;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #34d399;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip */
.share-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #34d399;
}

/* Animación del tooltip */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

/* Wrapper del botón favorito con tooltip */
.favorite-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip de favorito */
.favorite-tooltip {
  /* Anclado a la derecha: centrado se sale de la pantalla en móvil */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #fbbf24;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #fbbf24;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip favorito */
.favorite-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #fbbf24;
}

.share-tooltip.tooltip-fade-enter,
.share-tooltip.tooltip-fade-leave-to,
.favorite-tooltip.tooltip-fade-enter,
.favorite-tooltip.tooltip-fade-leave-to {
  transform: translateY(-4px);
}

/* Footer con botón */
.news-card__footer {
  display: flex;
  justify-content: center;
  padding: 0 16px 14px;
}

.news-card__button {
  padding: 8px 24px;
  background: white;
  border: none;
  border-radius: 50px;
  color: #1c2127;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 180px;
}

.news-card__button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
  background: #f3f4f6;
}

/* Botones de acción (favorito y leído) */
.news-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.news-card__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(28, 33, 39, 0.9);
  border: 1px solid #2d333b;
  color: #a0aec0;
  cursor: pointer;
  padding: 0;
  opacity: 0;
  transition: all 0.2s ease;
}

.news-card:hover .news-card__action-btn {
  opacity: 1;
}

.news-card__action-btn:hover {
  background: #4a9eff;
  color: white;
  border-color: #4a9eff;
  transform: scale(1.1);
}

/* Botón activo siempre visible con gradiente */
.news-card__action-btn--active {
  opacity: 1 !important;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  color: white;
  border-color: transparent;
}

.news-card__action-btn--active:hover {
  transform: scale(1.1) rotate(10deg);
}

.news-card__read-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(52, 211, 153, 0.2);
  color: #34d399;
  pointer-events: none;
}

/* Card leído - estilo más suave */
.news-card--read {
  opacity: 0.85;
}

.news-card--read .news-card__title {
  color: #a0aec0;
}

.news-card--read:hover {
  opacity: 1;
}

.news-card--read:hover .news-card__title {
  color: #e1e7ef;
}

/* Responsive design */
@media (max-width: 768px) {
  .news-card__title {
    font-size: 15px;
  }

  .news-card__category {
    font-size: 12px;
  }

  .metadata-date {
    font-size: 12px;
  }
}
</style>